'use client';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import cx from 'classnames';
import {
  myEquipmentListProps,
  Equipment,
  SearchParams
} from 'services/equipment/types';
import { selectEquipment } from 'services/equipment/actions';
import { searchFiltered } from 'services/equipment/utils';
import { ITEMS_PER_PAGE } from 'services/equipment/constants';
import { BlocksGridItem } from 'blocks-nextjs/blocks-web-components/components/grid-item/grid-item';
import { MyEquipmentMultipleCard } from 'components/my-equipment/my-equipment-multiple-card';
import { useMyEquipmentContext } from 'components/my-equipment/my-equipment-search-pagination-context';
import Pagination from 'components/my-equipment/pagination/pagination';
import { useBreakpoint, Conditional } from 'components/pcc-components';
import {
  CatGrid,
  CatLayoutSection,
  CatGridItem,
  CatLayout
} from 'components/blocks-components';
import { DISPLAY_PAGE_COUNT } from 'components/my-equipment/constants';
import SingleEquipmentCard from '../single-equipment-card';
import MyEquipmentLoader from '../my-equipment-loader';
import { PAGE_BREAKPOINTS } from 'constants/common';
import styles from '../my-equipment-wrapper/my-equipment-wrapper.module.scss';

const MyEquipmentGrid = ({
  selectedEquipment,
  serviceInsightsDetails,
  isRequestServiceFlag,
  userDetail,
  startTransition,
  isPending
}: myEquipmentListProps) => {
  const t = useTranslations();
  const isXL = useBreakpoint(PAGE_BREAKPOINTS.XL);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const {
    paginatedEquipmentList,
    totalPageCount,
    searchValue,
    page,
    clearSelection,
    selectedEquipmentIndex
  } = useMyEquipmentContext();

  const { uniqueIndex } = selectedEquipment || {};

  const isEnableSelectedCard =
    isXL &&
    selectedEquipmentIndex !== -1 &&
    searchFiltered(selectedEquipment, searchValue);

  const isGridVariant = useMemo(() => {
    if (isXL) {
      return isEnableSelectedCard ? '3up' : '5up';
    }
  }, [isEnableSelectedCard, isXL]);

  const selectCardOnClick = (
    event: React.MouseEvent<BlocksGridItem, MouseEvent>,
    equipment: Equipment
  ) => {
    event.preventDefault();
    const { id, serialNumber } = equipment;
    startTransition(async () => {
      await selectEquipment(id, serialNumber);
      clearSelection();
    });
  };

  return (
    <Conditional
      test={!isPending && isXL !== undefined}
      fallback={<MyEquipmentLoader isSearchBar={false} />}
    >
      <CatLayout
        gap={selectedEquipmentIndex > -1 ? 'lg' : 'none'}
        variant="right-sidebar"
        className={cx({
          [styles['equipment-container__selected-grid']]: isEnableSelectedCard,
          [styles['equipment-container__grid']]: !isEnableSelectedCard
        })}
      >
        <CatLayoutSection>
          <CatGrid
            variant={isGridVariant}
            gap={!isXL ? 'sm' : null}
            data-testid="grid-layout"
          >
            {paginatedEquipmentList?.map((eq, index) => (
              <CatGridItem
                key={eq.uniqueIndex}
                onClick={(event) => {
                  selectCardOnClick(event, eq);
                }}
              >
                <MyEquipmentMultipleCard
                  equipment={eq}
                  currentIndex={index}
                  searchTerm={searchValue}
                  selectTooltipText={t('SELECTED_EQUIPMENT')}
                  uniqueIndex={uniqueIndex}
                  isRequestServiceFlag={isRequestServiceFlag}
                  startTransition={startTransition}
                  userDetail={userDetail}
                  serviceInsightsDetails={serviceInsightsDetails}
                />
              </CatGridItem>
            ))}
          </CatGrid>
          <Conditional
            test={totalPageCount > DISPLAY_PAGE_COUNT}
            fallback={null}
          >
            <div
              className={`mt-4 ${!isLG && 'd-flex justify-content-center w-100'}`}
            >
              <Pagination
                searchParams={
                  {
                    page,
                    pageSize: ITEMS_PER_PAGE.toString()
                  } as SearchParams
                }
                productCount={totalPageCount}
              />
            </div>
          </Conditional>
        </CatLayoutSection>

        {isEnableSelectedCard && (
          <CatLayoutSection
            data-testid="selected-layout"
            behavior="sticky"
            top="140px"
            className={styles['sticky-layout-section']}
          >
            <SingleEquipmentCard
              equipment={selectedEquipment}
              isDetailsCard
              isRequestServiceFlag={isRequestServiceFlag}
              startTransition={startTransition}
              userDetail={userDetail}
              serviceInsightsDetails={serviceInsightsDetails}
            />
          </CatLayoutSection>
        )}
      </CatLayout>
    </Conditional>
  );
};
export default MyEquipmentGrid;
